import React, { useEffect, useState } from 'react';
import { pricingApi } from '../../services/api';
import { PlusIcon, TrashIcon } from 'components/icons';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/PageHeader';
import { SkeletonBlock } from '../../components/ui/Skeleton';

interface LaborCategory {
  name: string;
  baseRate: number | string;
  description?: string;
}

interface ProfileForm {
  fringeRate: string; // stored as percentage in UI ("30" = 0.30)
  overheadRate: string;
  gaRate: string;
  profitRate: string;
  escalationRate: string;
  odcMarkup: string;
  dcaaApproved: boolean;
  casCovered: boolean;
  auditPosture: string;
  laborCategories: LaborCategory[];
  notes: string;
}

const EMPTY: ProfileForm = {
  fringeRate: '',
  overheadRate: '',
  gaRate: '',
  profitRate: '',
  escalationRate: '',
  odcMarkup: '',
  dcaaApproved: false,
  casCovered: false,
  auditPosture: '',
  laborCategories: [],
  notes: '',
};

const pctToDecimal = (s: string): number | null => {
  if (s === '' || s == null) return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  return n / 100;
};

const decimalToPct = (d: any): string => {
  if (d == null || d === '') return '';
  const n = parseFloat(d);
  if (isNaN(n)) return '';
  return (n * 100).toFixed(2);
};

const PricingProfile: React.FC = () => {
  const [form, setForm] = useState<ProfileForm>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await pricingApi.getProfile();
      const p = res.data?.data;
      if (p) {
        setForm({
          fringeRate: decimalToPct(p.fringeRate),
          overheadRate: decimalToPct(p.overheadRate),
          gaRate: decimalToPct(p.gaRate),
          profitRate: decimalToPct(p.profitRate),
          escalationRate: decimalToPct(p.escalationRate),
          odcMarkup: decimalToPct(p.odcMarkup),
          dcaaApproved: !!p.dcaaApproved,
          casCovered: !!p.casCovered,
          auditPosture: p.auditPosture || '',
          laborCategories: Array.isArray(p.laborCategories) ? p.laborCategories : [],
          notes: p.notes || '',
        });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        fringeRate: pctToDecimal(form.fringeRate),
        overheadRate: pctToDecimal(form.overheadRate),
        gaRate: pctToDecimal(form.gaRate),
        profitRate: pctToDecimal(form.profitRate),
        escalationRate: pctToDecimal(form.escalationRate),
        odcMarkup: pctToDecimal(form.odcMarkup),
        dcaaApproved: form.dcaaApproved,
        casCovered: form.casCovered,
        auditPosture: form.auditPosture || null,
        laborCategories: form.laborCategories
          .filter((c) => c.name && c.baseRate !== '' && c.baseRate != null)
          .map((c) => ({
            name: c.name,
            baseRate: parseFloat(String(c.baseRate)) || 0,
            description: c.description || '',
          })),
        notes: form.notes || null,
      };
      await pricingApi.saveProfile(payload);
      toast.success('Pricing profile saved');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    setForm({ ...form, laborCategories: [...form.laborCategories, { name: '', baseRate: '' }] });
  };
  const updateCategory = (idx: number, key: keyof LaborCategory, value: any) => {
    const next = [...form.laborCategories];
    next[idx] = { ...next[idx], [key]: value };
    setForm({ ...form, laborCategories: next });
  };
  const removeCategory = (idx: number) => {
    setForm({ ...form, laborCategories: form.laborCategories.filter((_, i) => i !== idx) });
  };

  // Live preview: $50 base @ Y0 loaded rate
  const previewBase = 50;
  const fringe = parseFloat(form.fringeRate) / 100 || 0;
  const oh = parseFloat(form.overheadRate) / 100 || 0;
  const ga = parseFloat(form.gaRate) / 100 || 0;
  const profit = parseFloat(form.profitRate) / 100 || 0;
  const previewLoaded = previewBase * (1 + fringe) * (1 + oh) * (1 + ga) * (1 + profit);
  const previewMultiplier = previewBase > 0 ? (previewLoaded / previewBase).toFixed(3) : '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Decorative page-title icon dropped — colored icons now signal nav
          state and plan locks only, not decoration. */}
      <div className="mb-6">
        <PageHeader
          title="Pricing Profile"
          subtitle="Your reusable rate structure. The proposal writer pulls these numbers when generating Price/Cost narratives, and the per-proposal pricing table uses them to compute loaded rates. Set this once."
        />
      </div>

      {loading ? (
        <div className="space-y-4">
          <span className="sr-only" role="status">Loading pricing profile</span>
          <SkeletonBlock className="h-24" />
          <SkeletonBlock className="h-40" />
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <section className="bg-surface border border-border rounded-lg p-5">
            <h2 className="text-base font-semibold text-content mb-3">Indirect Rates</h2>
            <p className="text-xs text-content-muted mb-4">
              Enter as percentages (e.g. <code>30</code> means 30%). Math: loaded rate = base ×
              (1+fringe)(1+OH)(1+G&amp;A)(1+profit) × (1+escalation)<sup>year</sup>.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PctField label="Fringe" value={form.fringeRate} onChange={(v) => setForm({ ...form, fringeRate: v })} hint="applied to base labor" />
              <PctField label="Overhead" value={form.overheadRate} onChange={(v) => setForm({ ...form, overheadRate: v })} hint="applied to base + fringe" />
              <PctField label="G&A" value={form.gaRate} onChange={(v) => setForm({ ...form, gaRate: v })} hint="applied to base + fringe + OH" />
              <PctField label="Profit / Fee" value={form.profitRate} onChange={(v) => setForm({ ...form, profitRate: v })} hint="target margin" />
              <PctField label="Annual Escalation" value={form.escalationRate} onChange={(v) => setForm({ ...form, escalationRate: v })} hint="compounds per option year" />
              <PctField label="ODC Markup" value={form.odcMarkup} onChange={(v) => setForm({ ...form, odcMarkup: v })} hint="materials/ODCs only" />
            </div>

            <div className="mt-5 p-3 rounded bg-emerald-50 border border-emerald-200 text-sm">
              <span className="font-medium text-emerald-900">Quick check:</span>{' '}
              <span className="text-content">
                $50/hr base → ${previewLoaded.toFixed(2)} loaded (Y0) — multiplier {previewMultiplier}×
              </span>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-lg p-5">
            <h2 className="text-base font-semibold text-content mb-3">Audit Posture</h2>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.dcaaApproved} onChange={(e) => setForm({ ...form, dcaaApproved: e.target.checked })} />
                <span>DCAA-approved indirect rate proposal on file</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.casCovered} onChange={(e) => setForm({ ...form, casCovered: e.target.checked })} />
                <span>CAS-covered (Cost Accounting Standards)</span>
              </label>
              <div>
                <label className="block text-xs font-medium text-content mb-1">Audit posture statement</label>
                <input
                  type="text"
                  value={form.auditPosture}
                  onChange={(e) => setForm({ ...form, auditPosture: e.target.value })}
                  className="input"
                  placeholder="e.g., DCAA-audited indirect rate proposal FY2026"
                />
                <p className="text-xs text-content-muted mt-1">Used as a price-realism discriminator in proposal narratives.</p>
              </div>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-content">Labor Categories</h2>
              <button
                type="button"
                onClick={addCategory}
                className="inline-flex items-center gap-1 text-sm text-purple-700 hover:text-purple-900"
              >
                <PlusIcon className="w-4 h-4" /> Add
              </button>
            </div>
            <p className="text-xs text-content-muted mb-3">
              Reusable labor categories with base hourly rates. When adding a labor line item to a proposal,
              you can pick from this list to auto-fill the base rate.
            </p>
            {form.laborCategories.length === 0 ? (
              <p className="text-sm text-content-subtle italic">No labor categories yet — add your common roles.</p>
            ) : (
              <div className="space-y-2">
                {form.laborCategories.map((cat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Category name (e.g., Sr Project Manager)"
                      value={cat.name}
                      onChange={(e) => updateCategory(idx, 'name', e.target.value)}
                      className="input flex-1"
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-content-muted text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="50.00"
                        value={cat.baseRate}
                        onChange={(e) => updateCategory(idx, 'baseRate', e.target.value)}
                        className="input w-24"
                      />
                      <span className="text-content-muted text-sm">/hr</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCategory(idx)}
                      className="p-2 text-content-subtle hover:text-red-600"
                      title="Remove"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-surface border border-border rounded-lg p-5">
            <h2 className="text-base font-semibold text-content mb-3">Notes (internal)</h2>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
              placeholder="Anything you want to remember about your rate structure (audit dates, source documents, etc.)"
            />
          </section>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500 rounded-md hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const PctField: React.FC<{ label: string; value: string; onChange: (v: string) => void; hint?: string }> = ({
  label,
  value,
  onChange,
  hint,
}) => (
  <div>
    <label className="block text-xs font-medium text-content mb-1">{label}</label>
    <div className="flex items-center gap-1">
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input flex-1"
        placeholder="0.00"
      />
      <span className="text-content-muted text-sm">%</span>
    </div>
    {hint && <p className="text-xs text-content-subtle mt-1">{hint}</p>}
  </div>
);

export default PricingProfile;
